import { Component } from '@angular/core';
import {
  FluidCapacityCarryState,
  FluidCapacityGeometryMode,
  FluidCapacityHourComponent,
  FluidCapacityHourSlot,
  FluidCapacityRenderVariant,
} from '@clinic-platform/ui-components';

import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';

interface FluidCapacityPrototypeScenario {
  id: string;
  title: string;
  description: string;
  hourLabel: string;
  geometryMode: FluidCapacityGeometryMode;
  renderVariant: FluidCapacityRenderVariant;
  slots: FluidCapacityHourSlot[];
}

const baselineSlots: FluidCapacityHourSlot[] = [
  {
    id: 'fluid-1400',
    startMinute: 0,
    durationMinutes: 15,
    startLabel: '14:00',
    endLabel: '14:15',
    specialistAvailable: 5,
    specialistTotal: 5,
    deviceAvailable: 5,
    deviceTotal: 5,
    status: 'available',
  },
  {
    id: 'fluid-1415',
    startMinute: 15,
    durationMinutes: 15,
    startLabel: '14:15',
    endLabel: '14:30',
    specialistAvailable: 3,
    specialistTotal: 5,
    deviceAvailable: 5,
    deviceTotal: 5,
    status: 'available',
  },
  {
    id: 'fluid-1430',
    startMinute: 30,
    durationMinutes: 15,
    startLabel: '14:30',
    endLabel: '14:45',
    specialistAvailable: 5,
    specialistTotal: 5,
    deviceAvailable: 2,
    deviceTotal: 5,
    status: 'available',
  },
  {
    id: 'fluid-1445',
    startMinute: 45,
    durationMinutes: 15,
    startLabel: '14:45',
    endLabel: '15:00',
    specialistAvailable: 0,
    specialistTotal: 5,
    deviceAvailable: 5,
    deviceTotal: 5,
    status: 'available',
  },
];

const stressSlots: FluidCapacityHourSlot[] = [
  {
    id: 'fluid-1500',
    startMinute: 0,
    durationMinutes: 12,
    startLabel: '15:00',
    endLabel: '15:12',
    specialistAvailable: 4,
    specialistTotal: 4,
    deviceAvailable: 1,
    deviceTotal: 4,
    status: 'available',
  },
  {
    id: 'fluid-1512',
    startMinute: 12,
    durationMinutes: 18,
    startLabel: '15:12',
    endLabel: '15:30',
    specialistAvailable: 1,
    specialistTotal: 5,
    deviceAvailable: 5,
    deviceTotal: 5,
    status: 'available',
  },
  {
    id: 'fluid-1530',
    startMinute: 30,
    durationMinutes: 12,
    startLabel: '15:30',
    endLabel: '15:42',
    specialistAvailable: 5,
    specialistTotal: 5,
    deviceAvailable: 0,
    deviceTotal: 4,
    status: 'available',
  },
  {
    id: 'fluid-1542',
    startMinute: 42,
    durationMinutes: 18,
    startLabel: '15:42',
    endLabel: '16:00',
    specialistAvailable: 0,
    specialistTotal: 5,
    deviceAvailable: 5,
    deviceTotal: 5,
    status: 'available',
  },
];

const denseSlots: FluidCapacityHourSlot[] = [
  {
    id: 'fluid-1600',
    startMinute: 0,
    durationMinutes: 8,
    startLabel: '16:00',
    endLabel: '16:08',
    specialistAvailable: 2,
    specialistTotal: 3,
    deviceAvailable: 3,
    deviceTotal: 3,
    status: 'available',
  },
  {
    id: 'fluid-1608',
    startMinute: 8,
    durationMinutes: 7,
    startLabel: '16:08',
    endLabel: '16:15',
    specialistAvailable: 0,
    specialistTotal: 3,
    deviceAvailable: 3,
    deviceTotal: 3,
    status: 'available',
  },
  {
    id: 'fluid-1615',
    startMinute: 15,
    durationMinutes: 10,
    startLabel: '16:15',
    endLabel: '16:25',
    specialistAvailable: 3,
    specialistTotal: 4,
    deviceAvailable: 1,
    deviceTotal: 4,
    status: 'available',
  },
  {
    id: 'fluid-1625',
    startMinute: 25,
    durationMinutes: 5,
    startLabel: '16:25',
    endLabel: '16:30',
    specialistAvailable: 1,
    specialistTotal: 4,
    deviceAvailable: 1,
    deviceTotal: 4,
    status: 'available',
  },
  {
    id: 'fluid-1630',
    startMinute: 30,
    durationMinutes: 12,
    startLabel: '16:30',
    endLabel: '16:42',
    specialistAvailable: 4,
    specialistTotal: 4,
    deviceAvailable: 4,
    deviceTotal: 4,
    status: 'available',
  },
  {
    id: 'fluid-1642',
    startMinute: 42,
    durationMinutes: 8,
    startLabel: '16:42',
    endLabel: '16:50',
    specialistAvailable: 1,
    specialistTotal: 4,
    deviceAvailable: 0,
    deviceTotal: 4,
    status: 'available',
  },
  {
    id: 'fluid-1650',
    startMinute: 50,
    durationMinutes: 10,
    startLabel: '16:50',
    endLabel: '17:00',
    specialistAvailable: 2,
    specialistTotal: 4,
    deviceAvailable: 4,
    deviceTotal: 4,
    status: 'available',
  },
];

const capacityIntelligenceSlots: FluidCapacityHourSlot[] = [
  {
    id: 'fluid-1700',
    startMinute: 0,
    durationMinutes: 12,
    startLabel: '17:00',
    endLabel: '17:12',
    specialistAvailable: 0,
    specialistTotal: 4,
    deviceAvailable: 0,
    deviceTotal: 4,
    status: 'available',
    intelligence: {
      signal: 'expand',
      label: 'Expand',
      followUp: 'capacity',
      deviceRequiredTimePercent: 58,
      specialistIdlePercent: 0,
      deviceIdlePercent: 0,
    },
  },
  {
    id: 'fluid-1712',
    startMinute: 12,
    durationMinutes: 12,
    startLabel: '17:12',
    endLabel: '17:24',
    specialistAvailable: 0,
    specialistTotal: 4,
    deviceAvailable: 4,
    deviceTotal: 4,
    status: 'available',
    intelligence: {
      signal: 'hire-specialists',
      label: 'Need S',
      followUp: 'hire',
      deviceRequiredTimePercent: 50,
      specialistIdlePercent: 0,
      deviceIdlePercent: 50,
    },
  },
  {
    id: 'fluid-1724',
    startMinute: 24,
    durationMinutes: 12,
    startLabel: '17:24',
    endLabel: '17:36',
    specialistAvailable: 3,
    specialistTotal: 4,
    deviceAvailable: 0,
    deviceTotal: 4,
    status: 'available',
    intelligence: {
      signal: 'add-devices',
      label: 'Need D',
      followUp: 'devices',
      deviceRequiredTimePercent: 82,
      specialistIdlePercent: 42,
      deviceIdlePercent: 0,
    },
  },
  {
    id: 'fluid-1736',
    startMinute: 36,
    durationMinutes: 12,
    startLabel: '17:36',
    endLabel: '17:48',
    specialistAvailable: 1,
    specialistTotal: 4,
    deviceAvailable: 3,
    deviceTotal: 4,
    status: 'available',
    intelligence: {
      signal: 'mix-waste',
      label: 'Mix waste',
      followUp: 'procedure',
      deviceRequiredTimePercent: 48,
      specialistIdlePercent: 0,
      deviceIdlePercent: 55,
    },
  },
  {
    id: 'fluid-1748',
    startMinute: 48,
    durationMinutes: 12,
    startLabel: '17:48',
    endLabel: '18:00',
    specialistAvailable: 2,
    specialistTotal: 4,
    deviceAvailable: 2,
    deviceTotal: 4,
    status: 'available',
    intelligence: {
      signal: 'preference-skew',
      label: 'New S idle',
      followUp: 'preference',
      deviceRequiredTimePercent: 58,
      specialistIdlePercent: 48,
      deviceIdlePercent: 22,
      preferenceSkewPercent: 82,
    },
  },
];

function scenarioSlots(prefix: string, slots: FluidCapacityHourSlot[]): FluidCapacityHourSlot[] {
  return slots.map((slot) => ({
    ...slot,
    id: `${prefix}-${slot.id}`,
  }));
}

@Component({
  selector: 'app-appointment-fluid-capacity-prototype-page',
  standalone: true,
  imports: [
    PageHeaderComponent,
    FluidCapacityHourComponent,
  ],
  template: `
    <app-page-header
      title="Fluid Capacity Prototype"
      subtitle="Synthetic one-hour renderer for the resource-flow slot concept"
    />

    <section class="fluid-capacity-prototype">
      @for (scenario of scenarios; track scenario.id) {
      <div class="fluid-capacity-prototype__panel">
        <div class="fluid-capacity-prototype__panel-head">
          <div>
            <h3>{{ scenario.title }}</h3>
            <p>{{ scenario.description }}</p>
          </div>
          <div class="fluid-capacity-prototype__carry">
            Carry: {{ carryStates[scenario.id].overflowSlotId || 'none' }} / {{ carryStates[scenario.id].overflowMinutes }} min
          </div>
        </div>

        <lib-fluid-capacity-hour
          [hourLabel]="scenario.hourLabel"
          [minHeight]="76"
          [slots]="scenario.slots"
          [carryState]="carryStates[scenario.id]"
          [geometryMode]="scenario.geometryMode"
          [renderVariant]="scenario.renderVariant"
          (carryStateChange)="onCarryStateChanged(scenario.id, $event)"
        />

        <div class="fluid-capacity-prototype__legend">
          <span><i class="fluid-capacity-prototype__swatch fluid-capacity-prototype__swatch--device"></i> Devices</span>
          <span><i class="fluid-capacity-prototype__swatch fluid-capacity-prototype__swatch--specialist"></i> Specialists</span>
          <span><i class="fluid-capacity-prototype__swatch fluid-capacity-prototype__swatch--blocked"></i> Bottleneck</span>
        </div>
      </div>
      }
    </section>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      overflow-x: clip;
    }

    :host * {
      box-sizing: border-box;
    }

    .fluid-capacity-prototype {
      display: grid;
      gap: 16px;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      overflow-x: hidden;
    }

    .fluid-capacity-prototype__panel {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 16px;
      background: #ffffff;
      display: grid;
      gap: 16px;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      overflow: hidden;
    }

    .fluid-capacity-prototype__panel-head {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      align-items: start;
      min-width: 0;
    }

    .fluid-capacity-prototype__panel-head > div:first-child {
      min-width: 0;
    }

    .fluid-capacity-prototype__panel-head h3 {
      margin: 0;
      font-size: 16px;
      color: #111827;
    }

    .fluid-capacity-prototype__panel-head p {
      margin: 4px 0 0;
      font-size: 13px;
      color: #6b7280;
      max-width: 680px;
      overflow-wrap: anywhere;
    }

    .fluid-capacity-prototype__carry {
      padding: 6px 8px;
      border-radius: 8px;
      background: #f8fafc;
      border: 1px solid #e5e7eb;
      font-size: 12px;
      color: #374151;
      white-space: nowrap;
    }

    .fluid-capacity-prototype__legend {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      font-size: 12px;
      color: #374151;
    }

    .fluid-capacity-prototype__legend span {
      display: inline-flex;
      gap: 6px;
      align-items: center;
    }

    .fluid-capacity-prototype__swatch {
      width: 18px;
      height: 10px;
      border-radius: 999px;
      border: 1px solid rgba(15, 23, 42, 0.12);
    }

    .fluid-capacity-prototype__swatch--device {
      background: #93c5fd;
    }

    .fluid-capacity-prototype__swatch--specialist {
      background: #22c55e;
    }

    .fluid-capacity-prototype__swatch--blocked {
      background: #ef4444;
    }

    @media (max-width: 720px) {
      .fluid-capacity-prototype__panel-head {
        display: grid;
      }
    }
  `],
})
export class AppointmentFluidCapacityPrototypePageComponent {
  protected readonly scenarios: FluidCapacityPrototypeScenario[] = [
    {
      id: 'baseline',
      title: 'Adaptive split + soft ribbon flow',
      description: 'One hour, four synthetic 15-minute slots. Upper region is devices, lower region is specialists. The split shifts toward the bottleneck, while contiguous slot diagonals meet as one soft hour ribbon.',
      hourLabel: '14:',
      geometryMode: 'diagonal',
      renderVariant: 'standard',
      slots: scenarioSlots('baseline', baselineSlots),
    },
    {
      id: 'stress',
      title: 'Boundary bridge stress set',
      description: 'One synthetic hour with uneven slot durations and high-contrast transitions. This checks whether the boundary-gradient bridge remains useful when green, amber, and red pressure states alternate.',
      hourLabel: '15:',
      geometryMode: 'diagonal',
      renderVariant: 'standard',
      slots: scenarioSlots('stress', stressSlots),
    },
    {
      id: 'level-baseline',
      title: 'Level split baseline',
      description: 'Same 14:00 synthetic data rendered with a straight capacity level instead of diagonal geometry. This checks whether the resource balance reads faster as a level meter.',
      hourLabel: '14:',
      geometryMode: 'level',
      renderVariant: 'standard',
      slots: scenarioSlots('level-baseline', baselineSlots),
    },
    {
      id: 'level-stress',
      title: 'Level split stress set',
      description: 'Same stress data rendered as straight levels. This compares pure capacity readability against the diagonal flow version.',
      hourLabel: '15:',
      geometryMode: 'level',
      renderVariant: 'standard',
      slots: scenarioSlots('level-stress', stressSlots),
    },
    {
      id: 'hybrid-level-flow',
      title: 'Hybrid level + subtle flow',
      description: 'Level split keeps capacity readable, while a muted bridge gives a secondary continuity cue without becoming the main signal.',
      hourLabel: '14:',
      geometryMode: 'level',
      renderVariant: 'hybrid',
      slots: scenarioSlots('hybrid', baselineSlots),
    },
    {
      id: 'throughput-marker',
      title: 'Explicit throughput marker',
      description: 'Same diagonal geometry with a compact throughput bar that exposes min(devices, specialists) directly for faster operator scanning.',
      hourLabel: '15:',
      geometryMode: 'diagonal',
      renderVariant: 'throughput',
      slots: scenarioSlots('throughput', stressSlots),
    },
    {
      id: 'pressure-overlay',
      title: 'Independent bottleneck pressure',
      description: 'Resource fill still shows availability, while edge intensity and hatch overlays carry bottleneck pressure as a separate visual channel.',
      hourLabel: '15:',
      geometryMode: 'level',
      renderVariant: 'pressure',
      slots: scenarioSlots('pressure', stressSlots),
    },
    {
      id: 'soft-ribbon',
      title: 'Softened ribbon flow',
      description: 'Diagonal movement is dampened to preserve continuity without implying literal intra-slot capacity changes over time.',
      hourLabel: '14:',
      geometryMode: 'diagonal',
      renderVariant: 'soft-ribbon',
      slots: scenarioSlots('soft-ribbon', baselineSlots),
    },
    {
      id: 'soft-state-layer',
      title: 'Soft palette + operational state',
      description: 'A calmer palette reduces stress noise, while a tiny state rail adds stable, unstable, overloaded, recovery, or predicted saturation hints.',
      hourLabel: '16:',
      geometryMode: 'level',
      renderVariant: 'state-layer',
      slots: scenarioSlots('state-layer', denseSlots),
    },
    {
      id: 'dense-soft-palette',
      title: 'Dense compressed soft palette',
      description: 'Seven uneven slots test compressed scanning, rapid bottleneck alternation, high carry propagation, and lower-saturation pressure colors.',
      hourLabel: '16:',
      geometryMode: 'diagonal',
      renderVariant: 'soft-palette',
      slots: scenarioSlots('dense-soft', denseSlots),
    },
    {
      id: 'throughput-hour-rail',
      title: 'Throughput-first hour rail',
      description: 'Tests whether one quiet hour-level rail can become the primary scan signal for effective capacity while slots explain the cause.',
      hourLabel: '14:',
      geometryMode: 'level',
      renderVariant: 'throughput-rail',
      slots: scenarioSlots('throughput-rail', baselineSlots),
    },
    {
      id: 'throughput-primary-band',
      title: 'Throughput-first slot band',
      description: 'Makes effective throughput more dominant inside each slot, while device and specialist counts become supporting context.',
      hourLabel: '15:',
      geometryMode: 'level',
      renderVariant: 'throughput-band',
      slots: scenarioSlots('throughput-band', stressSlots),
    },
    {
      id: 'bottleneck-scan',
      title: 'Bottleneck preattentive scan',
      description: 'Tests edge pressure intensity and dead-slot desaturation so operators can detect problematic slots without reading labels.',
      hourLabel: '15:',
      geometryMode: 'level',
      renderVariant: 'bottleneck-scan',
      slots: scenarioSlots('bottleneck-scan', stressSlots),
    },
    {
      id: 'continuity-rail',
      title: 'Continuity rail',
      description: 'Adds a low-priority left-to-right continuity rail below the slots to show operational evolution without changing slot semantics.',
      hourLabel: '14:',
      geometryMode: 'level',
      renderVariant: 'continuity-rail',
      slots: scenarioSlots('continuity-rail', baselineSlots),
    },
    {
      id: 'medium-pressure',
      title: 'Medium pressure differentiation',
      description: 'Tests whether constrained slots can be distinguished from healthy and overloaded states without increasing color saturation.',
      hourLabel: '16:',
      geometryMode: 'level',
      renderVariant: 'medium-pressure',
      slots: scenarioSlots('medium-pressure', denseSlots),
    },
    {
      id: 'flow-focus',
      title: 'Flow-focused comparison',
      description: 'Keeps the softened flow language visible as a separate mental model for pressure propagation and continuity comparison.',
      hourLabel: '15:',
      geometryMode: 'diagonal',
      renderVariant: 'focus-flow',
      slots: scenarioSlots('flow-focus', stressSlots),
    },
    {
      id: 'operational-candidate',
      title: 'Operational candidate',
      description: 'Combines level split, cleaner throughput band, and top/bottom bottleneck edges as the current working candidate for staff scanning.',
      hourLabel: '15:',
      geometryMode: 'level',
      renderVariant: 'operational-candidate',
      slots: scenarioSlots('operational-candidate', stressSlots),
    },
    {
      id: 'operational-candidate-dense',
      title: 'Operational candidate dense',
      description: 'Same candidate visual language against dense uneven slots and rapid constrained/blocked alternation.',
      hourLabel: '16:',
      geometryMode: 'level',
      renderVariant: 'operational-candidate',
      slots: scenarioSlots('operational-candidate-dense', denseSlots),
    },
    {
      id: 'capacity-intelligence',
      title: 'Capacity intelligence signals',
      description: 'Synthetic admin analytics slot language: business success, bottleneck side, idle/waste, procedure mix, preference skew, and follow-up hint.',
      hourLabel: '17:',
      geometryMode: 'level',
      renderVariant: 'capacity-intelligence',
      slots: scenarioSlots('capacity-intelligence', capacityIntelligenceSlots),
    },
    {
      id: 'capacity-intelligence-compact',
      title: 'Capacity intelligence compact',
      description: 'Compact admin analytics cell: business success rail, bottleneck edge, one diagnosis chip, and one follow-up reason without micro-dashboard bars.',
      hourLabel: '17:',
      geometryMode: 'level',
      renderVariant: 'capacity-intelligence-compact',
      slots: scenarioSlots('capacity-intelligence-compact', capacityIntelligenceSlots),
    },
    {
      id: 'capacity-intelligence-maturity',
      title: 'Capacity intelligence compact - semantic maturity pass',
      description: 'Same compact structure with refined chip confidence, rail morphology, dead-slot silence, and opportunity/problem distinction.',
      hourLabel: '17:',
      geometryMode: 'level',
      renderVariant: 'capacity-intelligence-maturity',
      slots: scenarioSlots('capacity-intelligence-maturity', capacityIntelligenceSlots),
    },
  ];

  protected carryStates: Record<string, FluidCapacityCarryState> = this.scenarios.reduce<Record<string, FluidCapacityCarryState>>(
    (carryStates, scenario) => ({
      ...carryStates,
      [scenario.id]: {
        overflowSlotId: null,
        overflowMinutes: 0,
      },
    }),
    {},
  );

  protected onCarryStateChanged(scenarioId: string, value: FluidCapacityCarryState): void {
    this.carryStates = {
      ...this.carryStates,
      [scenarioId]: value,
    };
  }
}
